import { Box, Tooltip } from "@mui/material";
import type { Kks, PidStatus } from "@shared/types/domain";
import { StatusChip } from "./StatusChip";

interface Props {
  status: PidStatus;
  kks: Kks[];
}

export function PidStatusTooltip({ status, kks }: Props) {
  const total = kks.length;
  const verified = kks.filter((k) => k.verified).length;
  const withComponent = kks.filter(
    (k) => k.component && k.component.trim() !== "Unknown",
  ).length;
  const componentPct =
    total > 0 ? Math.round((withComponent / total) * 100) : 0;
  const unverified = total - verified;
  const spofPending = kks.filter((k) => k.spof && !k.spof_approved).length;

  let tooltip: string | null = null;

  if (status === "required_spof_review") {
    const requiredSpofCount = kks.filter(
      (k) => k.spof && k.required_spof,
    ).length;
    const spofNotReady = kks.filter(
      (k) =>
        k.required_spof &&
        !(
          k.required &&
          k.verified &&
          k.spof &&
          k.spof_approved &&
          k.component &&
          k.component !== "Unknown"
        ),
    ).length;
    const lines: string[] = [
      `Mark KKS as Required SPOF (currently ${requiredSpofCount} marked).`,
      `Each Required SPOF KKS must also be: Required, Verified, SPOF, SPOF Approved, and have a component assigned before an admin can lock.`,
    ];
    if (spofNotReady > 0)
      lines.push(`${spofNotReady} Required SPOF KKS still not ready.`);
    else if (requiredSpofCount > 0)
      lines.push("All Required SPOF KKS are ready to lock.");
    tooltip = lines.join("\n");
  } else if (status === "verification") {
    const lines: string[] = [];
    if (unverified > 0)
      lines.push(
        `${unverified} KKS still need to be verified (${verified}/${total} done)`,
      );
    if (componentPct < 95)
      lines.push(
        `Component must be set on ≥ 95% of KKS (currently ${componentPct}%)`,
      );
    tooltip =
      lines.length > 0
        ? `To reach SPOF Approval:\n• ${lines.join("\n• ")}`
        : "All verification requirements met";
  } else if (status === "spof_approval") {
    tooltip =
      spofPending > 0
        ? `To reach Approved:\n• ${spofPending} SPOF KKS still need SPOF approval`
        : "All SPOF approval requirements met";
  }

  return (
    <Tooltip
      title={
        tooltip ? (
          <Box
            component="span"
            sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}
          >
            {tooltip}
          </Box>
        ) : (
          ""
        )
      }
      placement="bottom"
      disableHoverListener={!tooltip}
    >
      <Box component="span" sx={{ flexShrink: 0 }}>
        <StatusChip status={status} />
      </Box>
    </Tooltip>
  );
}
